import { useState } from 'react';
import type { Schedule } from '../api/types.ts';
import { useShop } from '../app/boot.tsx';
import { useShopPaths } from '../app/nav.tsx';
import { fieldErrors } from '../lib/errors.ts';
import { Banner, Button, Card, Chip, Empty, Field, Loading, Page, Sheet } from '../ui/index.tsx';
import { useConflictRunner } from './conflicts.tsx';
import { useSchedule } from './useSchedule.ts';

type ServiceRow = Schedule['services'][number];
const DURATIONS = Array.from({ length: 16 }, (_, i) => (i + 1) * 15);

export function Services() {
  const { data, error, reload } = useSchedule();
  const { go } = useShopPaths();
  const [editing, setEditing] = useState<ServiceRow | 'new' | null>(null);
  return (
    <Page title="บริการ" back={() => go('/staff/manage')} actions={<Button className="btn-sm" onClick={() => setEditing('new')}>เพิ่มบริการ</Button>}>
      {error && <Banner kind="error">{error}</Banner>}
      {!data ? <Loading /> : data.services.length === 0 ? <Empty title="ยังไม่มีบริการ" action={{ label: 'เพิ่มบริการ', onClick: () => setEditing('new') }} /> : (
        <div className="list">
          {data.services.map((s) => (
            <Card key={s.id} as="button" onClick={() => setEditing(s)}>
              <div className="row-between">
                <div className="card-title">{s.name}</div>
                <span className="price">{s.price.toLocaleString('th-TH')} บาท</span>
              </div>
              <div className="row" style={{ marginTop: 4 }}>
                <span className="muted small">{s.durationMin} นาที · ช่าง {s.barberIds.length} คน</span>
                {!s.active && <Chip tone="muted">ปิดรับจอง</Chip>}
              </div>
            </Card>
          ))}
        </div>
      )}
      {editing && data && <ServiceSheet service={editing === 'new' ? null : editing} schedule={data} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload(); }} />}
    </Page>
  );
}

function ServiceSheet({ service, schedule, onClose, onSaved }: { service: ServiceRow | null; schedule: Schedule; onClose: () => void; onSaved: () => void }) {
  const { api } = useShop();
  const conflicts = useConflictRunner();
  const [name, setName] = useState(service?.name ?? '');
  const [durationMin, setDurationMin] = useState(service?.durationMin ?? 30);
  const [price, setPrice] = useState(service ? String(service.price) : '');
  const [barberIds, setBarberIds] = useState<string[]>(service?.barberIds ?? []);
  const [active, setActive] = useState(service?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toggle = (id: string) => setBarberIds((xs) => (xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]));

  async function save() {
    setErrors({});
    const p = Number(price);
    if (price.trim() === '' || !Number.isInteger(p) || p < 0) { setErrors({ price: 'ราคาเป็นจำนวนเต็มบาท' }); return; }
    await conflicts.run(async (resolutions) => {
      try {
        await api.staff.upsertService({ id: service?.id, name, durationMin, price: p, barberIds, active, sortOrder: service?.sortOrder, resolutions });
      } catch (e) {
        const f = fieldErrors(e);
        if (Object.keys(f).length) { setErrors(f); return; }
        throw e;
      }
    }, { onDone: onSaved });
  }

  return (
    <Sheet open title={service ? 'แก้ไขบริการ' : 'เพิ่มบริการ'} onClose={onClose}>
      <Field label="ชื่อบริการ" error={errors.name}>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
      </Field>
      <Field label="ระยะเวลา" error={errors.durationMin}>
        <select value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))}>
          {DURATIONS.map((d) => <option key={d} value={d}>{d} นาที</option>)}
        </select>
      </Field>
      <Field label="ราคา (บาท)" error={errors.price}>
        <input type="number" min={0} step={1} inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} />
      </Field>
      <p className="section-title">ช่างที่ทำบริการนี้</p>
      {schedule.barbers.map((b) => (
        <label key={b.id} className="check">
          <input type="checkbox" checked={barberIds.includes(b.id)} onChange={() => toggle(b.id)} />
          <span>{b.name}</span>
        </label>
      ))}
      <label className="check" style={{ marginTop: 8 }}>
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        <span>เปิดรับจอง</span>
      </label>
      <p className="muted small" style={{ margin: '8px 0 16px' }}>เปลี่ยนราคาหรือเวลาจะไม่กระทบการจองที่มีอยู่</p>
      <Button block onClick={() => void save()}>บันทึก</Button>
      {conflicts.dialog}
    </Sheet>
  );
}
